// src/screens/CreatorFlameDetailScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import './CreatorFlameDetailScreen.css';
import backIcon from '../assets/backspace-icon.png';
import CreatorFlameExplainerDrawer from '../components/popups/CreatorFlameExplainerDrawer';

const n = (v) => Number(v || 0);

// ✅ show full tiny scores like 0.000061
const nice = (v, digits = 2) =>
  n(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

function windowLabel(w) {
  const key = String(w || '').toLowerCase();
  if (key === '7d') return 'Last 7 days';
  if (key === '30d') return 'Last 30 days';
  if (key === '60d') return 'Last 60 days';
  if (key === '90d') return 'Last 90 days';
  return 'Recent';
}

export default function CreatorFlameDetailScreen() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const windowParam = searchParams.get('window') || '60d';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setErr(null);

      const qs = new URLSearchParams();
      qs.set('username', username);
      qs.set('window', windowParam);

      const res = await api.get(`/badges/creator-flame/detail?${qs.toString()}`);
      setData(res?.data || {});
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, windowParam]);

  const user = data?.user || { username };
  const meta = data?.meta || {};
  const posts = Array.isArray(data?.posts) ? data.posts : [];

  // Flexible field parsing for old + new backend shapes
  const likes = n(
    meta.likes ??
      meta.likes60d ??
      meta.likes30d ??
      meta.likes7d
  );

  const comments = n(
    meta.comments ??
      meta.comments60d ??
      meta.comments30d ??
      meta.comments7d
  );

  const tipCnt = n(
    meta.tipCount ??
      meta.tipCount60d ??
      meta.tipCount30d ??
      meta.tipCount7d
  );

  const tipPi = n(
    meta.tipPi ??
      meta.tipPi60d ??
      meta.tipPi30d ??
      meta.tipPi7d
  );

  // Score + rank
  const score = Number.isFinite(n(meta.score))
    ? n(meta.score)
    : Number.isFinite(n(meta.engagementScore))
      ? n(meta.engagementScore)
      : 0;

  const rankIndex = data?.rank?.index;
  const rankTotal = data?.rank?.total;

  // Simple bars (0..1)
  const clamp01 = (x) => Math.max(0, Math.min(1, n(x)));
  const bar = (x) => `${clamp01(x) * 100}%`;

  // ✅ Use softer display-only engagement score from backend
  // Fallback to strict engageNorm if backend not updated yet
  const engagementLevel = clamp01(
    meta.engageBarScore ??
    meta.engageNorm ??
    0
  );

  // Keep current behavior for these two
  const tipsLevel = clamp01(Math.max(n(meta.tipPiNorm), n(meta.tipCountNorm)));
  const consistencyLevel = clamp01(
    meta.consistencyScore ??
    meta.volumeFactor ??
    0
  );

  const summaryRows = useMemo(
    () => [
      { title: 'Engagement', value: engagementLevel },
      { title: 'Tips', value: tipsLevel },
      { title: 'Consistency', value: consistencyLevel },
    ],
    [engagementLevel, tipsLevel, consistencyLevel]
  );

  return (
    <div className="cfd-page" role="main" aria-label="Creator Flame">
      {/* Simple sticky header */}
      <div className="cfd-header cfd-header--sticky">
        <img
          src={backIcon}
          alt="Back"
          className="cfd-back"
          onClick={() => navigate(-1)}
        />
        <div className="cfd-title-wrap">
          <h2 className="cfd-title">Creator Flame</h2>
        </div>
      </div>

      <div className="cfd-scroll" id="cfd-scroll">
        {loading && <div className="cfd-loading">Loading…</div>}
        {!loading && err && <div className="cfd-error">{String(err)}</div>}

        {!loading && !err && (
          <>
            {/* Identity */}

<div className="cfd-user">
  <img
    className="cfd-avatar"
    src={user.profilePic || '/placeholder-profile.png'}
    alt={user.username}
  />
  <div className="cfd-user-meta">
    <div className="cfd-username">@{user.username}</div>
    <div className="cfd-name">{user.name || user.username}</div>
  </div>
  <button
    type="button"
    className="cfd-view-profile"
    onClick={() => navigate(`/user/${encodeURIComponent(user.username)}`)}
  >
    View profile
  </button>
</div>

            {/* Score card */}
            <div className="cfd-card">
              <div className="cfd-window">
                <strong>{windowLabel(data?.window || windowParam)}</strong>
              </div>

              <div className="cfd-grid">
                <div className="cfd-k">Score</div>
                <div className="cfd-v cfd-score">{nice(score, 6)}</div>

                {(rankIndex || rankIndex === 0) && (
                  <>
                    <div className="cfd-k">Rank</div>
                    <div className="cfd-v">
                      #{rankIndex} {rankTotal ? `of ${rankTotal}` : ''}
                    </div>
                  </>
                )}

                {data?.earnedAt && (
                  <>
                    <div className="cfd-k">Earned</div>
                    <div className="cfd-v">
                      {new Date(data.earnedAt).toLocaleDateString()}
                    </div>
                  </>
                )}

                {data?.expiresAt && (
                  <>
                    <div className="cfd-k">Expires</div>
                    <div className="cfd-v">
                      {new Date(data.expiresAt).toLocaleDateString()}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* What helped */}
            <div className="cfd-card">
              <div className="cfd-section-title">What helped your score</div>

              {summaryRows.map((row) => (
                <div key={row.title} className="cfd-comp">
                  <div className="cfd-comp-top">
                    <div className="cfd-comp-title">{row.title}</div>
                  </div>

                  <div className="cfd-rail">
                    <div className="cfd-fill" style={{ width: bar(row.value) }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Simple activity */}
            <div className="cfd-card">
              <div className="cfd-section-title">Your activity</div>
              <div className="cfd-grid">
                <div className="cfd-k">Likes</div>
                <div className="cfd-v">{likes}</div>

                <div className="cfd-k">Comments</div>
                <div className="cfd-v">{comments}</div>

                <div className="cfd-k">Tips</div>
                <div className="cfd-v">{tipCnt}</div>

                <div className="cfd-k">Tips (Pi)</div>
                <div className="cfd-v">{nice(tipPi, 6)} Pi</div>
              </div>
            </div>

            {/* Posts counted */}
            <div className="cfd-card">
              <div className="cfd-section-title">Posts counted</div>

              {(posts?.length || 0) === 0 ? (
                <div className="cfd-empty">No posts found in this window.</div>
              ) : (
                <div className="cfd-posts">
                  {posts.map((p) => (
                    <div key={p.postId} className="cfd-post">
                      {!!p.text && <div className="cfd-post-text">{p.text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Explainer button */}
            <div className="cfd-card">
              <button
                className="cfd-explainer-btn"
                type="button"
                onClick={() => setExplainerOpen(true)}
              >
                How this score works
              </button>
            </div>
          </>
        )}
      </div>

      <CreatorFlameExplainerDrawer
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
        score={score}
        windowLabel={data?.window || windowParam}
        likes={likes}
        comments={comments}
        tipCnt={tipCnt}
        tipPi={tipPi}
        rankIndex={rankIndex}
        rankTotal={rankTotal}
      />
    </div>
  );
}
